import { AttachFile, Close, EditNote, Minimize, Send } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	createFeedback,
	FeedbackApiError,
	type CreateFeedbackInput,
	type FeedbackUploadInput,
} from "../../Feedback/feedbackApi.ts";
import {
	feedbackScreenshotName,
	optimizeFeedbackScreenshot,
} from "../../Feedback/feedbackCapture.ts";
import { useUiI18n } from "../../I18n";
import {
	clearFeedbackDraft,
	readFeedbackDraft,
	saveFeedbackDraft,
} from "./feedbackDraftStorage.ts";
import { FeedbackRichEditor, type FeedbackRichEditorHandle } from "./FeedbackRichEditor.tsx";
import "./feedbackComposer.css";

interface FeedbackComposerProps {
	open: boolean;
	screenshot: string | null;
	onMinimize: () => void;
	onDiscard: () => void;
	onDraftChange: (hasDraft: boolean) => void;
	onCreated: () => void;
}

export const FeedbackComposer: FC<FeedbackComposerProps> = ({
	open,
	screenshot,
	onMinimize,
	onDiscard,
	onDraftChange,
	onCreated,
}) => {
	const { t } = useUiI18n();
	const initialDraftRef = useRef(readFeedbackDraft());
	const [title, setTitle] = useState(initialDraftRef.current?.title ?? "");
	const [contentHtml, setContentHtml] = useState(initialDraftRef.current?.contentHtml ?? "");
	const [context, setContext] = useState<CreateFeedbackInput["context"] | null>(
		initialDraftRef.current?.context ?? null,
	);
	const [uploads, setUploads] = useState<FeedbackUploadInput[]>(
		initialDraftRef.current?.uploads ?? [],
	);
	const [submitting, setSubmitting] = useState(false);
	const [mediaBusy, setMediaBusy] = useState(false);
	const editorRef = useRef<FeedbackRichEditorHandle | null>(null);
	const hasScreenshot = uploads.some((upload) => upload.kind === "screenshot");
	const capturedScreenshotRef = useRef<string | null>(
		initialDraftRef.current?.uploads.some((upload) => upload.kind === "screenshot")
			? "restored"
			: null,
	);

	useEffect(() => {
		if (!open) return;
		setMediaBusy(false);
		setContext((current) => {
			if (current) return current;
			const matchId = window.location.pathname.match(/^\/matches\/([^/]+)/)?.[1];
			return {
				url: window.location.href,
				path: `${window.location.pathname}${window.location.search}`,
				viewportWidth: window.innerWidth,
				viewportHeight: window.innerHeight,
				userAgent: navigator.userAgent,
				...(matchId ? { matchId } : {}),
			};
		});
		if (screenshot && capturedScreenshotRef.current !== screenshot) {
			capturedScreenshotRef.current = screenshot;
			setUploads((current) => [
				...current.filter((upload) => upload.kind !== "screenshot"),
				{
					id: crypto.randomUUID(),
					name: feedbackScreenshotName(screenshot),
					kind: "screenshot",
					dataUrl: screenshot,
				},
			]);
		}
	}, [open, screenshot]);

	useEffect(() => {
		const hasDraft = Boolean(context || title || contentHtml || uploads.length);
		if (!hasDraft) return;
		saveFeedbackDraft({
			title,
			contentHtml,
			context,
			uploads,
			minimized: !open,
			updatedAt: Date.now(),
		});
		onDraftChange(true);
	}, [contentHtml, context, onDraftChange, open, title, uploads]);

	const resetDraft = (): void => {
		clearFeedbackDraft();
		setTitle("");
		setContentHtml("");
		setContext(null);
		setUploads([]);
		capturedScreenshotRef.current = null;
		onDraftChange(false);
	};

	const discard = (): void => {
		resetDraft();
		onDiscard();
	};

	const submit = async (): Promise<void> => {
		const currentContentHtml = editorRef.current?.getHtml() ?? contentHtml;
		const currentContentText = editorRef.current?.getText() ?? "";
		if (!title.trim() || !currentContentText.trim()) {
			toast.warn(t("feedback.required"));
			return;
		}
		setSubmitting(true);
		try {
			const preparedUploads = await Promise.all(
				uploads.map(async (upload) => {
					if (upload.kind !== "screenshot") return upload;
					const dataUrl = await optimizeFeedbackScreenshot(upload.dataUrl);
					return { ...upload, dataUrl, name: feedbackScreenshotName(dataUrl) };
				}),
			);
			await createFeedback({
				title: title.trim(),
				contentHtml: currentContentHtml,
				contentText: currentContentText,
				context: context ?? {
					url: window.location.href,
					path: `${window.location.pathname}${window.location.search}`,
					viewportWidth: window.innerWidth,
					viewportHeight: window.innerHeight,
					userAgent: navigator.userAgent,
				},
				uploads: preparedUploads,
			});
			toast.success(t("feedback.created"));
			resetDraft();
			onCreated();
		} catch (error) {
			const message =
				error instanceof FeedbackApiError && error.code === "title_and_description_required"
					? t("feedback.required")
					: error instanceof FeedbackApiError && error.code === "upload_too_large"
						? t("feedback.error.uploadTooLarge")
						: error instanceof FeedbackApiError && error.code === "unsupported_upload_type"
							? t("feedback.error.unsupportedUpload")
							: t("feedback.createFailed");
			toast.error(message);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			className="feedback-composer"
			open={open}
			onClose={submitting || mediaBusy ? undefined : onMinimize}
			fullWidth
			maxWidth="md"
		>
			<DialogTitle className="feedback-composer__header" component="div">
				<Box className="feedback-composer__emblem" aria-hidden="true">
					<EditNote />
				</Box>
				<Box className="feedback-composer__heading">
					<Typography className="feedback-composer__eyebrow" variant="overline">
						{t("feedback.eyebrow")}
					</Typography>
					<Typography className="feedback-composer__title" component="h2">
						{t("feedback.new")}
					</Typography>
					<Typography className="feedback-composer__intro">{t("feedback.intro")}</Typography>
				</Box>
				<IconButton
					className="feedback-composer__window-action"
					aria-label={t("feedback.minimize")}
					onClick={onMinimize}
					disabled={submitting || mediaBusy}
				>
					<Minimize />
				</IconButton>
				<IconButton
					className="feedback-composer__window-action"
					aria-label={t("feedback.discard")}
					onClick={discard}
					disabled={submitting || mediaBusy}
				>
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent className="feedback-composer__content">
				<Stack spacing={2}>
					<TextField
						className="feedback-composer__subject"
						label={t("feedback.title")}
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						inputProps={{ maxLength: 180 }}
						autoFocus
						fullWidth
					/>
					<Box className="feedback-composer__prompt">
						<Typography className="feedback-composer__prompt-title">
							{t("feedback.description")}
						</Typography>
						<Typography className="feedback-composer__guidance">
							{t("feedback.guidance")}
						</Typography>
					</Box>
					<FeedbackRichEditor
						ref={editorRef}
						value={contentHtml}
						onChange={setContentHtml}
						onUpload={(upload) => setUploads((current) => [...current, upload])}
						onMediaBusyChange={setMediaBusy}
					/>
					<Box className="feedback-composer__context">
						<AttachFile fontSize="small" aria-hidden="true" />
						<Typography variant="caption">
							{hasScreenshot ? t("feedback.screenshotAttached") : t("feedback.urlAttached")}
							{uploads.length > 0
								? ` · ${t("feedback.attachmentsCount", { count: uploads.length })}`
								: ""}
						</Typography>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions className="feedback-composer__actions">
				<Button
					className="feedback-composer__send"
					variant="contained"
					startIcon={<Send />}
					onClick={() => void submit()}
					disabled={submitting || mediaBusy}
				>
					{mediaBusy
						? t("feedback.editor.processingMedia")
						: submitting
							? t("feedback.sending")
							: t("feedback.send")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
